import type { PaySnapshot, PayWindow } from "../../results/types";
import { deltaTone, pctChange } from "./results";

const DAYS_PER_MONTH = 30.44;
const BEFORE = "the 4 weeks before automatic BTB";

export interface WindowFigures { commissionPerHourCents: number | null; costPerHourCents: number | null; topUpPerMonthCents: number | null }

const money = (cents: number) => `$${(Math.abs(cents) / 100).toFixed(2)}`;
export const wholeMoney = (cents: number) => `$${Math.round(Math.abs(cents) / 100).toLocaleString("en-US")}`;
const topUp = (wageCents: number, minutes: number, commissionCents: number) => Math.max(0, (wageCents * minutes) / 60 - commissionCents);

export function windowFigures(w: PayWindow, wageCents: number | null): WindowFigures {
  const minutes = w.months.reduce((n, m) => n + m.minutes, 0);
  const commission = w.months.reduce((n, m) => n + m.commissionCents, 0);
  if (minutes === 0) return { commissionPerHourCents: null, costPerHourCents: null, topUpPerMonthCents: null };
  const hours = minutes / 60;
  const commissionPerHourCents = Math.round(commission / hours);
  if (wageCents === null) return { commissionPerHourCents, costPerHourCents: null, topUpPerMonthCents: null };
  const total = w.months.reduce((n, m) => n + topUp(wageCents, m.minutes, m.commissionCents), 0);
  return {
    commissionPerHourCents,
    costPerHourCents: Math.round((commission + total) / hours),
    topUpPerMonthCents: Math.round(total / (w.window.days / DAYS_PER_MONTH)),
  };
}

export interface StaffRow { staffId: string; name: string; minutes: number; commissionPerHourCents: number | null; changeCents: number | null; topUpCents: number | null }

export function staffRows(snapshot: PaySnapshot, wageCents: number | null): StaffRow[] {
  const rows = snapshot.staff.map((s) => {
    const since = snapshot.since.months.filter((m) => m.staffId === s.staffId);
    const before = snapshot.before.months.filter((m) => m.staffId === s.staffId);
    const sum = (ms: typeof since, k: "minutes" | "commissionCents") => ms.reduce((n, m) => n + m[k], 0);
    const minutes = sum(since, "minutes");
    const beforeMinutes = sum(before, "minutes");
    const commissionPerHourCents = minutes === 0 ? null : Math.round(sum(since, "commissionCents") / (minutes / 60));
    const beforePerHour = beforeMinutes === 0 ? null : Math.round(sum(before, "commissionCents") / (beforeMinutes / 60));
    return {
      staffId: s.staffId, name: s.name, minutes, commissionPerHourCents,
      changeCents: commissionPerHourCents === null || beforePerHour === null ? null : commissionPerHourCents - beforePerHour,
      topUpCents: wageCents === null ? null : Math.round(since.reduce((n, m) => n + topUp(wageCents, m.minutes, m.commissionCents), 0)),
    };
  });
  return rows.sort((a, b) => (b.commissionPerHourCents ?? -Infinity) - (a.commissionPerHourCents ?? -Infinity));
}

function weeksUnder(w: PayWindow, wageCents: number): { under: number; total: number } {
  const worked = w.weeks.filter((k) => k.minutes > 0);
  return { under: worked.filter((k) => k.commissionCents / (k.minutes / 60) < wageCents).length, total: worked.length };
}

export interface PaySavings { perHourCents: number; perMonthCents: number }

/**
 * What the owner saves on esthetician pay since BTB: the drop in cost per hour on shift (commission + top-up)
 * times the recent hours, per month. Negative when the cost per hour went up. Null without a wage or hours.
 */
export function paySavings(before: PayWindow, since: PayWindow, wageCents: number | null): PaySavings | null {
  if (wageCents === null || since.window.days <= 0) return null;
  const b = windowFigures(before, wageCents).costPerHourCents;
  const s = windowFigures(since, wageCents).costPerHourCents;
  if (b === null || s === null) return null;
  const hours = since.months.reduce((n, m) => n + m.minutes, 0) / 60;
  return { perHourCents: b - s, perMonthCents: Math.round(((b - s) * hours) / (since.window.days / DAYS_PER_MONTH)) };
}

/** Told from the owner's side (Austin, 2026-10-29): a lower cost per hour is money saved, a higher one is estheticians earning more. */
export function paySummary(before: PayWindow, since: PayWindow, wageCents: number | null): { tone: "success" | "neutral"; text: string } | null {
  const head = "Since BTB went live,";
  const savings = paySavings(before, since, wageCents);
  let flat: boolean;
  let text: string;
  if (savings) {
    const b = windowFigures(before, wageCents).costPerHourCents!;
    flat = deltaTone(pctChange(b - savings.perHourCents, b)) === "neutral";
    text = flat
      ? `${head} your esthetician pay per hour on shift is about the same as in ${BEFORE}.`
      : savings.perHourCents > 0
        ? `${head} you're saving about ${wholeMoney(savings.perMonthCents)} a month on esthetician pay: ${money(savings.perHourCents)} less per hour on shift than in ${BEFORE}.`
        : `${head} your estheticians earn ${money(savings.perHourCents)} more an hour on shift than in ${BEFORE}.`;
  } else {
    const b = windowFigures(before, null).commissionPerHourCents;
    const s = windowFigures(since, null).commissionPerHourCents;
    if (b === null || s === null) return null;
    flat = deltaTone(pctChange(s, b)) === "neutral";
    text = flat
      ? `${head} you pay about the same in commission per hour on shift as in ${BEFORE}.`
      : s < b
        ? `${head} you pay ${money(s - b)} less in commission per hour on shift than in ${BEFORE}.`
        : `${head} your estheticians earn ${money(s - b)} more an hour on shift than in ${BEFORE}.`;
  }
  if (wageCents !== null) {
    const bw = weeksUnder(before, wageCents);
    const sw = weeksUnder(since, wageCents);
    if (bw.total > 0 && sw.total > 0) text += ` Weeks under minimum wage: ${bw.under} of ${bw.total} before, ${sw.under} of ${sw.total} since.`;
  }
  return { tone: flat ? "neutral" : "success", text };
}

export function weeklySeries(snapshot: PaySnapshot): { x: string[]; values: (number | null)[] } {
  const value = (k: { minutes: number; commissionCents: number }) => (k.minutes > 0 ? k.commissionCents / 100 / (k.minutes / 60) : null);
  const b = snapshot.before.weeks;
  const s = snapshot.since.weeks;
  return {
    x: [...b.map((k) => k.from), "weeks in between not shown", ...s.map((k) => k.from)],
    values: [...b.map(value), null, ...s.map(value)],
  };
}
